import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ArrowRight, BellRing, Check, CheckCircle2, Copy, Loader2, Mail, MessageCircle, Send, Star, XCircle } from 'lucide-react';
import { api, formatDate } from '../../lib/api';
import { useCentrosResena } from '../../lib/centrosResena';
import { telefonoConPrefijo } from '../../lib/utils';
import { abrirPestanaYa, avisarCosteRetoque, avisarSaludoNeutro, costeRetoqueDesconocido, enviarMensajesMasivo, llevarPestanaA, mensajeCita, nombreCanal } from '../../lib/citasApi';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

/**
 * Panel de recordatorios (Phase 27.17): citas del día elegido con su estado.
 * Los WhatsApp por cita son los MISMOS que en el resumen de la cita de las
 * agendas (2026-08-26: «deben salir los mismos botones») —
 * Confirmación, Recordatorio y Pedir reseña —, llamando a los mismos
 * endpoints por cita para que el texto sea idéntico en las dos puertas
 * permitidas. En quirófano el servidor ya devuelve la confirmación de
 * CIRUGÍA y, como recordatorio, la guía de «mis pasos» (puntos 6 y 8): los
 * botones aparte «Cirugía: reserva» / «Cirugía: 1 sem. antes» daban esos
 * mismos textos y se quitaron (revisión 2026-09-03).
 *
 * Envío en bloque (2026-09-03, punto 3): desde el botón
 * Recordatorios de la vista del día se mandan las confirmaciones o los
 * recordatorios de TODAS las citas de la agenda seleccionada o de las que se
 * marquen. Cada cita lleva su plantilla (presencial / online / cirugía: la
 * elige el servidor, la misma que el botón de la cita) y la fila la enseña.
 * El día y la agenda vienen de la vista de agenda (`date`, `agendas`).
 *
 * Cómo salen: si el servidor tiene la API de Meta, POST /enviar-masivo los
 * manda todos y aquí solo se enseña el resultado. Si no (hoy), el navegador
 * solo deja abrir UNA pestaña por clic, así que se hace una COLA asistida:
 * el primer wa.me se abre en el mismo clic del botón, se marca enviado, y
 * el botón «Siguiente» abre el siguiente hasta acabar. El formulario aparte
 * (confirmación o recordatorio con documentación pendiente; en el
 * recordatorio desde el 2026-09-08, punto 13) es un paso más de la
 * cola para el mismo paciente, como el segundo toast del resumen de la cita.
 *
 * Reenvío (verificación de cierre 2026-09-05): una fila «Enviado por WhatsApp»
 * se puede marcar a mano, y entonces se pregunta «Ya se envió: ¿reenviar?» y
 * el bloque va con `reenviar` (sin él el servidor las omite).
 *
 * Selección (2026-09-08, punto 14): fuera «Seleccionar todas» y
 * fuera la preselección de las pendientes. Con todo marcado de entrada, un
 * clic en «Enviar recordatorios» abría el PRIMER wa.me y el resto quedaban
 * esperando a «Siguiente», y recepción entendía que habían salido todos
 * cuando solo había salido uno. Ahora no hay nada marcado al abrir: se
 * marcan las filas una a una y la cola las abre una a una (una pestaña por
 * clic es límite del navegador, no de WhatsApp). Cerrar el panel con la cola
 * a medias pide confirmación.
 *
 * Etiqueta «Enviado por WhatsApp» (mismo punto 14): la marca la guarda el
 * servidor (POST /whatsapp-enviado) y devuelve lo guardado; la fila se pinta
 * con ESA respuesta, no con una recarga. Antes se marcaba sin esperar y se
 * recargaba el listado a la vez, y según cuál llegara antes la fila salía
 * «Pendiente» aunque el mensaje hubiera salido (sobre todo la última de la
 * cola). Si la marca falla, se avisa: la fila sigue pendiente de verdad.
 */
const ETIQUETA_TIPO = { presencial: 'Presencial', online: 'Online', cirugia: 'Cirugía', retoque: 'Retoque' };

/**
 * Las DOS marcas de la cita, cada una con su casilla (revisión 2026-09-21).
 * A una paciente se le mandan dos mensajes con semanas de por medio —la
 * confirmación al darle la cita y el recordatorio el día antes— y hasta hoy
 * los dos escribían en `recordatorio_enviado_at`: quien mandaba la
 * confirmación dejaba la cita marcada como si ya tuviera recordatorio, y a
 * partir de ahí el recordatorio no salía.
 *
 * El orden es el de la vida de la cita: primero la confirmación.
 */
const MARCAS = [
  { tipo: 'confirmacion', campoAt: 'confirmacion_enviada_at', campoCanal: 'confirmacion_canal', que: 'Confirmación', participio: 'Enviada' },
  { tipo: 'recordatorio', campoAt: 'recordatorio_enviado_at', campoCanal: 'recordatorio_canal', que: 'Recordatorio', participio: 'Enviado' },
];
const CAMPOS_MARCA = MARCAS.flatMap((m) => [m.campoAt, m.campoCanal]);
const marcaDe = (tipo) => MARCAS.find((m) => m.tipo === tipo) || MARCAS[1];
/** ¿Esta fila es un RETOQUE? Lo dice el servidor en `tipo_mensaje` (la misma
 * plantilla que va a usar al enviar; routers/recordatorios_router._tipo_mensaje),
 * no se adivina aquí por el concepto. Hace falta para el rótulo del botón:
 * desde el cambio de la clínica (2026-09-17) el recordatorio de un retoque manda
 * las pautas preoperatorias, NO la guía de «mis pasos» con su enlace, y
 * `es_quirofano` no distingue una cosa de la otra. */
const esFilaRetoque = (it) => it.tipo_mensaje === 'retoque';
const NOMBRE_ENVIO = { confirmacion: 'confirmaciones', recordatorio: 'recordatorios' };
/** «1 confirmación enviada», «3 recordatorios enviados»: número y género bien. */
const contar = (n, tipo, participio = false) => {
  const [uno, varios, enviado] = tipo === 'confirmacion'
    ? ['confirmación', 'confirmaciones', 'enviada']
    : ['recordatorio', 'recordatorios', 'enviado'];
  return `${n} ${n === 1 ? uno : varios}${participio ? ` ${enviado}${n === 1 ? '' : 's'}` : ''}`;
};

export default function RecordatoriosDialog({ open, onOpenChange, onChanged, agendas = [], date: fechaAgenda = '' }) {
  const manana = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    // Fecha LOCAL: con toISOString (UTC), entre las 00:00 y la 01:00/02:00
    // españolas el panel abría con las citas de hoy (revisión 2026-08-27).
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const [date, setDate] = useState(fechaAgenda || manana());
  // '' = todas las agendas que el usuario puede ver (como antes del filtro).
  const [agendaId, setAgendaId] = useState(agendas[0]?.id || '');
  const [data, setData] = useState({ items: [], email_automatico: false, whatsapp_api: false });
  const [cargando, setCargando] = useState(false);
  // Un fallo al cargar el día NO se puede pintar como «Sin citas» (revisión
  // 2026-09-21): recepción leía el estado vacío, cerraba el panel y ese día
  // nadie mandaba ningún recordatorio.
  const [error, setError] = useState('');
  const [enviandoEmail, setEnviandoEmail] = useState(null); // cita_id
  const [busy, setBusy] = useState(null); // `${cita_id}:${accion}` | 'masivo'
  const [resenaFor, setResenaFor] = useState(null); // item o null
  // Centros de reseña de Configuración: sin ninguno no se ofrece «Pedir
  // reseña»; con uno se manda directo; con varios, se elige.
  const centrosResena = useCentrosResena(open);
  // Citas marcadas a mano para el envío en bloque: nada de entrada (punto 14).
  const [seleccion, setSeleccion] = useState(() => new Set());
  // Cola asistida: { tipo, pasos: [{cita_id, patient_name, url, marcar}], indice }
  const [cola, setCola] = useState(null);
  const [omitidas, setOmitidas] = useState([]); // resultados no enviados del último bloque

  // Al ABRIR, el panel se pone en el día y la agenda que se están viendo:
  // «la agenda que esté seleccionada en ese momento» (punto 3). Solo
  // en la transición cerrado→abierto: si la página recarga sus agendas con
  // el panel abierto, no se pisa lo que recepción haya cambiado ni la cola.
  const estabaAbierto = useRef(false);
  useEffect(() => {
    if (open && !estabaAbierto.current) {
      setDate(fechaAgenda || manana());
      setAgendaId(agendas[0]?.id || '');
      setSeleccion(new Set());
      setCola(null);
      setOmitidas([]);
    }
    estabaAbierto.current = open;
  }, [open, fechaAgenda, agendas]);

  // Marcas de envío (POST /whatsapp-enviado) en vuelo: cualquier recarga del
  // listado espera a que terminen, para no leer la cita antes de la marca.
  const marcasEnVuelo = useRef(new Set());

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      if (marcasEnVuelo.current.size) await Promise.allSettled([...marcasEnVuelo.current]);
      const params = { date };
      if (agendaId) params.agenda_id = agendaId;
      const res = await api.get('/api/recordatorios', { params });
      setError('');
      setData(res.data);
    } catch (e) {
      // El toast se va solo y no vuelve; lo que queda en pantalla tiene que
      // decir la verdad. Y las citas del día anterior se quitan: dejarlas
      // sería enseñar las de OTRO día como si fueran las de este.
      setError(e?.response?.data?.detail || 'No se pudieron cargar los recordatorios');
      setData((prev) => ({ ...prev, items: [] }));
      toast.error(e?.response?.data?.detail || 'No se pudieron cargar los recordatorios');
    } finally {
      setCargando(false);
    }
  }, [date, agendaId]);

  useEffect(() => { if (open) cargar(); }, [open, cargar]);
  // Otro día u otra agenda: se empieza sin nada marcado.
  useEffect(() => { setSeleccion(new Set()); }, [date, agendaId]);

  /** Pinta en la fila la marca tal como la ha guardado el servidor. Solo las
   *  casillas que vengan en la respuesta: la del mensaje que se acaba de
   *  mandar, sin tocar la del otro (revisión 2026-09-21). */
  const aplicarMarca = (citaId, marca) => {
    const puesto = {};
    CAMPOS_MARCA.forEach((campo) => { if (marca?.[campo]) puesto[campo] = marca[campo]; });
    if (!Object.keys(puesto).length) return;
    setData((prev) => ({
      ...prev,
      items: prev.items.map((it) => (it.cita_id === citaId ? { ...it, ...puesto } : it)),
    }));
  };

  /** ¿Trae la respuesta del servidor alguna marca que pintar? */
  const traeMarca = (d) => CAMPOS_MARCA.some((campo) => d?.[campo]);

  /**
   * Deja constancia del envío en la cita y pinta la fila con lo que el
   * servidor ha guardado (punto 14). Si un servidor antiguo no devolviera la
   * marca, se recarga el listado (ya sin carrera: la marca ha terminado).
   *
   * `tipo` dice CUÁL de los dos mensajes ha salido (revisión 2026-09-21): sin
   * él, el servidor marcaba siempre el recordatorio y la confirmación que
   * recepción manda al dar la cita dejaba a la paciente sin el aviso del día
   * antes. Va como parámetro de la petición, no pegado a la ruta.
   */
  const marcarEnviado = (citaId, nombre = '', tipo = 'recordatorio') => {
    let recargar = false;
    const marca = (async () => {
      try {
        const { data: d } = await api.post(`/api/recordatorios/${citaId}/whatsapp-enviado`, null, { params: { tipo } });
        if (traeMarca(d)) aplicarMarca(citaId, d);
        else recargar = true;
      } catch (e) {
        toast.warning(`No se ha podido registrar el envío${nombre ? ` de ${nombre}` : ''}: la fila sigue como pendiente`, {
          description: e?.response?.data?.detail || 'Vuelve a mandarlo o márcalo al recargar.',
          duration: 12000,
        });
      }
    })();
    marcasEnVuelo.current.add(marca);
    // La recarga (si hace falta) va DESPUÉS de soltar la marca: `cargar`
    // espera a las marcas en vuelo y esta no puede esperarse a sí misma.
    return marca
      .finally(() => marcasEnVuelo.current.delete(marca))
      .then(() => (recargar ? cargar() : undefined));
  };

  const enviarConfirmacionORecordatorio = async (item, tipo) => {
    const ventana = abrirPestanaYa();
    setBusy(`${item.cita_id}:${tipo}`);
    try {
      const d = await mensajeCita(item.cita_id, tipo);
      if (!d?.whatsapp_url) {
        ventana?.close();
        toast.error('El paciente no tiene un teléfono válido para WhatsApp');
        return;
      }
      llevarPestanaA(ventana, d.whatsapp_url);
      // Saludo «Estimado/a» (cierre 2026-09-05): aviso, sin frenar el envío.
      if (d.saludo_neutro) avisarSaludoNeutro(item.patient_name);
      // Retoque sin coste conocido (2026-09-08): la confirmación lleva el
      // bloque de pago; aviso para revisarlo antes de mandar.
      if (costeRetoqueDesconocido(d)) avisarCosteRetoque(item.patient_name);
      // El formulario del paciente con documentación pendiente viaja DENTRO
      // del mensaje (2026-09-09): un solo wa.me por cita. El
      // segundo mensaje que se abría aparte desde el 2026-09-08 no se
      // mandaba, porque exigía pulsar otro botón después del primero.
      await marcarEnviado(item.cita_id, item.patient_name, tipo);
      onChanged?.();
    } catch (e) {
      ventana?.close();
      toast.error(e?.response?.data?.detail || 'No se pudo preparar el mensaje');
    } finally {
      setBusy(null);
    }
  };

  const enviarResena = async (item, centro) => {
    const ventana = abrirPestanaYa();
    setResenaFor(null);
    setBusy(`${item.cita_id}:resena`);
    try {
      const { data: d } = await api.get(`/api/recordatorios/cita/${item.cita_id}/resena`, { params: { centro } });
      if (!d?.whatsapp_url) {
        ventana?.close();
        toast.error('El paciente no tiene un teléfono válido para WhatsApp');
        return;
      }
      llevarPestanaA(ventana, d.whatsapp_url);
      // La reseña saluda como los demás mensajes (24/9/2026): si
      // sale «Estimado/a», el mismo aviso, sin frenar el envío.
      if (d.saludo_neutro) avisarSaludoNeutro(item.patient_name);
    } catch (e) {
      ventana?.close();
      toast.error(e?.response?.data?.detail || 'No se pudo preparar el mensaje de reseña');
    } finally {
      setBusy(null);
    }
  };

  const enviarEmail = async (item) => {
    setEnviandoEmail(item.cita_id);
    try {
      await api.post(`/api/recordatorios/${item.cita_id}/email`);
      toast.success(`Email enviado a ${item.patient_name}`);
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo enviar el email');
    } finally {
      setEnviandoEmail(null);
    }
  };

  const copiarEnlace = async (item) => {
    try {
      await navigator.clipboard.writeText(item.enlace_confirmacion);
      toast.success('Enlace de confirmación copiado');
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  // ---------------------------------------------------------------- selección

  const enviable = (it) => it.status === 'activa';
  const enviables = useMemo(() => data.items.filter(enviable).map((it) => it.cita_id), [data.items]);
  // Solo lo marcado a mano, fila a fila (punto 14): ni «todas» ni pendientes
  // de entrada.
  const seleccionadas = useMemo(() => enviables.filter((id) => seleccion.has(id)), [seleccion, enviables]);
  const estaSeleccionada = (id) => seleccionadas.includes(id);
  const alternar = (id, marcar) => {
    setSeleccion((prev) => {
      const s = new Set(prev);
      if (marcar) s.add(id); else s.delete(id);
      return s;
    });
  };

  // -------------------------------------------------------------- envío en bloque

  /** Un paso de la cola por cita: UN mensaje, que lleva dentro el formulario
   *  si al paciente le toca (2026-09-09). */
  const pasosDe = (resultados) => resultados
    .filter((r) => r.estado === 'preparado' && r.whatsapp_url)
    // `saludo_neutro` viaja con el paso: el aviso salta al abrir ESE wa.me,
    // no todos de golpe (cierre 2026-09-05).
    .map((r) => ({ cita_id: r.cita_id, patient_name: r.patient_name, url: r.whatsapp_url, marcar: true, saludo_neutro: !!r.saludo_neutro, coste_desconocido: costeRetoqueDesconocido(r) }));

  const abrirPaso = (paso, ventana, tipo) => {
    llevarPestanaA(ventana, paso.url);
    if (paso.saludo_neutro) avisarSaludoNeutro(paso.patient_name);
    if (paso.coste_desconocido) avisarCosteRetoque(paso.patient_name);
    // La fila se pinta con la respuesta del servidor en cuanto llega, y la
    // marca es la del mensaje que lleva ESTE bloque (revisión 2026-09-21).
    if (paso.marcar) marcarEnviado(paso.cita_id, paso.patient_name, tipo);
  };

  const enviarMasivo = async (tipo) => {
    if (!seleccionadas.length || busy || cola) return;
    // Filas ya enviadas marcadas a mano: se pregunta y el bloque va con
    // `reenviar` (sin él el servidor las omite como «Ya enviado»).
    // «Ya enviada» es respecto AL MENSAJE DE ESTE BLOQUE (revisión
    // 2026-09-21): una cita con la confirmación mandada no es un reenvío
    // cuando lo que se está mandando es el recordatorio.
    const { campoAt } = marcaDe(tipo);
    const yaEnviadas = data.items.filter((it) => it[campoAt] && seleccionadas.includes(it.cita_id));
    if (yaEnviadas.length && !window.confirm(`Ya se envió a ${yaEnviadas.map((it) => it.patient_name).join(', ')}: ¿reenviar?`)) return;
    // Con la API de Meta el envío es inmediato y sin vuelta atrás: se pregunta.
    if (data.whatsapp_api && !window.confirm(`¿Enviar ahora ${contar(seleccionadas.length, tipo)} por WhatsApp?`)) return;
    // Sin API, la primera pestaña se abre DENTRO del clic (si no, el navegador la bloquea).
    const ventana = data.whatsapp_api ? null : abrirPestanaYa();
    setBusy('masivo');
    setOmitidas([]);
    try {
      const d = await enviarMensajesMasivo(seleccionadas, tipo, yaEnviadas.length > 0);
      const noEnviadas = d.resultados.filter((r) => r.estado !== 'preparado' && r.estado !== 'enviado');
      setOmitidas(noEnviadas);
      if (d.canal === 'api') {
        if (d.enviados) toast.success(`${contar(d.enviados, tipo, true)} por WhatsApp`);
        if (noEnviadas.length) toast.warning(`${noEnviadas.length} sin enviar (ver abajo)`);
        // Por la API el mensaje ya ha salido: el aviso sirve para corregir la
        // ficha antes del siguiente (cierre 2026-09-05).
        const neutros = d.resultados.filter((r) => r.estado === 'enviado' && r.saludo_neutro);
        if (neutros.length) avisarSaludoNeutro(neutros.map((r) => r.patient_name).join(', '));
        const sinCoste = d.resultados.filter((r) => r.estado === 'enviado' && costeRetoqueDesconocido(r));
        if (sinCoste.length) avisarCosteRetoque(sinCoste.map((r) => r.patient_name).join(', '));
        // Cada fila enviada trae la marca que el servidor ha guardado (punto 14),
        // y es la del mensaje de ESTE bloque: en un bloque de CONFIRMACIONES el
        // servidor ya no devuelve `recordatorio_enviado_at` sino
        // `confirmacion_enviada_at` (revisión 2026-09-21), así que se mira
        // cualquiera de las dos. Sin test propio a propósito: aquí el `cargar()`
        // de la línea siguiente reemplaza `data` entera y tapa esta marca, así
        // que no se puede ver desde fuera. Se deja coherente para que el día
        // que se quite esa recarga no quede una trampa escrita.
        d.resultados.filter((r) => r.estado === 'enviado' && traeMarca(r)).forEach((r) => aplicarMarca(r.cita_id, r));
        cargar();
        onChanged?.();
        return;
      }
      const pasos = pasosDe(d.resultados);
      if (!pasos.length) {
        ventana?.close();
        toast.error('Ninguna de las citas seleccionadas se puede enviar');
        return;
      }
      abrirPaso(pasos[0], ventana, tipo);
      if (pasos.length === 1) {
        toast.success(contar(1, tipo, true));
        cargar();
        onChanged?.();
      } else {
        // Que no parezca que han salido todos (punto 14): solo ha salido el
        // primero; el resto esperan a «Siguiente», uno por clic.
        toast.info(`Abierto 1 de ${pasos.length}: pulsa «Siguiente» para el próximo`, {
          description: 'Solo se ha abierto el primer mensaje; los demás salen uno a uno.',
          duration: 8000,
        });
        setCola({ tipo, pasos, indice: 1 });
      }
    } catch (e) {
      ventana?.close();
      toast.error(e?.response?.data?.detail || 'No se pudieron preparar los mensajes');
    } finally {
      setBusy(null);
    }
  };

  const siguientePaso = () => {
    if (!cola) return;
    const paso = cola.pasos[cola.indice];
    abrirPaso(paso, abrirPestanaYa(), cola.tipo);
    if (cola.indice + 1 >= cola.pasos.length) {
      terminarCola(true);
    } else {
      setCola({ ...cola, indice: cola.indice + 1 });
    }
  };

  const terminarCola = (completa = false) => {
    if (completa && cola) {
      toast.success(contar(cola.pasos.filter((p) => p.marcar).length, cola.tipo, true));
    }
    setCola(null);
    cargar();
    onChanged?.();
  };

  const estado = (item) => {
    if (item.paciente_confirmacion === 'confirmada') {
      return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300"><CheckCircle2 className="w-3 h-3 mr-1" /> Confirmada</Badge>;
    }
    if (item.paciente_confirmacion === 'cancelada' || item.status === 'cancelada') {
      return <Badge className="bg-red-100 text-red-700 border-red-300"><XCircle className="w-3 h-3 mr-1" /> Cancelada</Badge>;
    }
    // Una etiqueta por mensaje que haya salido, diciendo CUÁL y CUÁNDO
    // (revisión 2026-09-21). Antes había una sola, «Enviado por WhatsApp»,
    // para los dos: una fila marcada hace tres semanas al dar la cita se leía
    // igual que la de un recordatorio de ayer, y recepción se saltaba a la
    // paciente fiándose de eso.
    const puestas = MARCAS.filter((m) => item[m.campoAt]);
    if (!puestas.length) {
      return <Badge variant="secondary" className="bg-amber-100 text-amber-800 border-amber-300">Pendiente</Badge>;
    }
    // Y si salió la confirmación pero NO el recordatorio, se dice: es el caso
    // en el que la cita parecía avisada y no lo estaba.
    const faltaRecordatorio = !item.recordatorio_enviado_at;
    return (
      <>
        {puestas.map((m) => (
          <Badge key={m.tipo} variant="secondary" className="bg-[#2E6E5E]/10 text-[#13231E] border-[#2E6E5E]/30">
            <Check className="w-3 h-3 mr-1" />
            {m.que} · {m.participio} por {nombreCanal(item[m.campoCanal])} el {formatDate(item[m.campoAt])}
          </Badge>
        ))}
        {faltaRecordatorio && (
          <Badge variant="secondary" className="bg-amber-100 text-amber-800 border-amber-300">Recordatorio pendiente</Badge>
        )}
      </>
    );
  };

  const tipoBadge = (item) => (
    <Badge variant="outline" className="text-[10px] uppercase tracking-wide" data-testid={`recordatorio-tipo-${item.cita_id}`}>
      {ETIQUETA_TIPO[item.tipo_mensaje] || item.tipo_mensaje || '—'}
    </Badge>
  );

  const pasoActual = cola ? cola.pasos[cola.indice] : null;
  const bloqueado = !!busy || !!cola;

  // Cerrar con la cola a medias (punto 14): se pregunta, porque lo que queda
  // por abrir NO ha salido y al reabrir la cola no se recupera.
  const cerrar = (abierto) => {
    if (!abierto && cola) {
      const quedan = cola.pasos.length - cola.indice;
      if (!window.confirm(`Quedan ${quedan} mensajes sin abrir: ¿cerrar igualmente? No se enviarán.`)) return;
      terminarCola(false);
    }
    onOpenChange?.(abierto);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={cerrar}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="recordatorios-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#13231E]">
            <BellRing className="w-4 h-4 text-[#2E6E5E]" /> Recordatorios de cita
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-1">Día</div>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 w-[170px]" data-testid="recordatorios-fecha" disabled={!!cola} />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-1">Agenda</div>
            <select
              value={agendaId}
              onChange={(e) => setAgendaId(e.target.value)}
              disabled={!!cola}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
              data-testid="recordatorios-agenda"
            >
              {agendas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              <option value="">Todas las agendas</option>
            </select>
          </div>
          <div className="text-xs text-muted-foreground pb-2">
            {data.whatsapp_api
              ? 'WhatsApp conectado: los envíos en bloque salen solos del servidor.'
              : data.email_automatico
                ? 'Email automático activo: los recordatorios de mañana salen solos.'
                : 'WhatsApp asistido: pulsa el botón y solo tendrás que dar a enviar. (El email automático se activa configurando SMTP en el servidor.)'}
          </div>
        </div>

        {/* Envío en bloque: a las marcadas a mano (nada de entrada, punto 14). */}
        {data.items.length > 0 && !cargando && (
          <div className="flex items-center gap-3 flex-wrap rounded-md border border-border bg-muted/40 px-3 py-2" data-testid="recordatorios-masivo">
            {/* Sin «Seleccionar todas» (punto 14): se marcan las filas una a
                una y salen una a una. */}
            <span className="text-xs text-muted-foreground tabular-nums" data-testid="recordatorios-contador">
              {seleccionadas.length} de {enviables.length} seleccionadas
            </span>
            {seleccionadas.length === 0 && enviables.length > 0 && (
              <span className="text-xs text-muted-foreground" data-testid="recordatorios-ayuda-seleccion">
                Marca las citas a las que quieras mandar el mensaje; se abren de una en una.
              </span>
            )}
            <div className="flex items-center gap-1.5 flex-wrap sm:ml-auto">
              <Button
                size="sm"
                className="h-8 bg-[#2E6E5E] hover:bg-[#245548] text-white"
                onClick={() => enviarMasivo('confirmacion')}
                disabled={bloqueado || seleccionadas.length === 0}
                title="Mandar la confirmación de su cita a cada seleccionada (cada una con su plantilla)"
                data-testid="recordatorios-enviar-confirmaciones"
              >
                {busy === 'masivo' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                Enviar confirmaciones
              </Button>
              <Button
                size="sm"
                className="h-8 bg-slate-700 hover:bg-slate-800 text-white"
                onClick={() => enviarMasivo('recordatorio')}
                disabled={bloqueado || seleccionadas.length === 0}
                title="Mandar el recordatorio de su cita a cada seleccionada (cada una con su plantilla)"
                data-testid="recordatorios-enviar-recordatorios"
              >
                {busy === 'masivo' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                Enviar recordatorios
              </Button>
            </div>
          </div>
        )}

        {/* Cola asistida: una pestaña por clic. */}
        {cola && pasoActual && (
          <div className="flex items-center gap-3 flex-wrap rounded-md border border-[#2E6E5E]/40 bg-[#2E6E5E]/5 px-3 py-2" data-testid="recordatorios-cola">
            <div className="text-sm min-w-0 flex-1">
              <div className="font-medium text-[#13231E]">
                {cola.indice} de {cola.pasos.length} abiertos · {NOMBRE_ENVIO[cola.tipo]}
              </div>
              <div className="text-xs text-muted-foreground truncate">
                Siguiente: {pasoActual.patient_name}
              </div>
            </div>
            <Button size="sm" className="h-8 bg-[#2E6E5E] hover:bg-[#245548] text-white" onClick={siguientePaso} data-testid="recordatorios-siguiente">
              <ArrowRight className="w-4 h-4 mr-1" /> Siguiente
            </Button>
            <Button size="sm" variant="ghost" className="h-8" onClick={() => terminarCola(false)} data-testid="recordatorios-terminar">
              Terminar
            </Button>
          </div>
        )}

        {omitidas.length > 0 && (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="recordatorios-omitidas">
            <div className="font-medium mb-1">Sin enviar ({omitidas.length}):</div>
            <ul className="list-disc pl-4 space-y-0.5">
              {omitidas.map((r) => (
                <li key={r.cita_id}>{r.patient_name || r.cita_id}{r.start ? ` (${r.start})` : ''} — {r.motivo}</li>
              ))}
            </ul>
          </div>
        )}

        {cargando ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Cargando…</div>
        ) : error ? (
          /* Un fallo del servidor NO se enseña como el dato «ese día no hay
             citas» (revisión 2026-09-21): con «Sin citas» delante, recepción
             cerraba el panel y ese día nadie confirmaba su cita. El vacío de
             verdad es solo el de abajo, cuando el servidor ha contestado. */
          <div className="py-8 text-center space-y-3" data-testid="recordatorios-error">
            <p className="text-sm text-[#13231E] font-medium">No se han podido cargar las citas de este día</p>
            <p className="text-xs text-muted-foreground">{error}</p>
            <Button size="sm" variant="outline" className="h-8" onClick={cargar} data-testid="recordatorios-reintentar">
              Reintentar
            </Button>
          </div>
        ) : data.items.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            Sin citas el {formatDate(date)}.
          </div>
        ) : (
          <div className="divide-y divide-border">
            {data.items.map((it) => (
              <div key={it.cita_id} className="py-2.5 flex items-center gap-3 flex-wrap" data-testid={`recordatorio-row-${it.cita_id}`}>
                <Checkbox
                  checked={estaSeleccionada(it.cita_id)}
                  onCheckedChange={(v) => alternar(it.cita_id, v === true)}
                  disabled={bloqueado || !enviable(it)}
                  aria-label={`Seleccionar ${it.patient_name}`}
                  data-testid={`recordatorio-check-${it.cita_id}`}
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-[#13231E] truncate">
                    <span className="tabular-nums mr-2">{it.start}</span>{it.patient_name}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {it.concept_name} · {it.agenda_name}{it.telefono ? ` · ${telefonoConPrefijo(it.telefono)}` : ''}
                  </div>
                </div>
                {tipoBadge(it)}
                {estado(it)}
                {enviable(it) && (
                <div className="flex items-center gap-1 flex-wrap">
                  <Button
                    size="sm" variant="outline"
                    className="h-8 text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                    onClick={() => enviarConfirmacionORecordatorio(it, 'confirmacion')}
                    disabled={bloqueado}
                    title={it.es_quirofano
                      ? 'Mandarle la confirmación de su cirugía: pautas y pago de la reserva'
                      : 'Mandarle la confirmación de su cita'}
                    data-testid={`recordatorio-confirmacion-${it.cita_id}`}
                  >
                    {busy === `${it.cita_id}:confirmacion`
                      ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      : <CheckCircle2 className="w-4 h-4 mr-1" />}
                    Confirmación
                  </Button>
                  <Button
                    size="sm" variant="outline"
                    className="h-8 text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                    onClick={() => enviarConfirmacionORecordatorio(it, 'recordatorio')}
                    disabled={bloqueado}
                    // Un retoque manda las pautas, no la guía (la clínica,
                    // 2026-09-17): el rótulo tiene que decirlo, porque desde
                    // aquí se envía a ciegas, sin ver el texto.
                    title={esFilaRetoque(it)
                      ? 'Mandarle el recordatorio de su retoque: las pautas preoperatorias (sin la guía de mis pasos)'
                      : it.es_quirofano
                        ? 'Mandarle el recordatorio de su cirugía: la guía de mis pasos con su enlace'
                        : 'Mandarle el recordatorio de su cita'}
                    data-testid={`recordatorio-recordatorio-${it.cita_id}`}
                  >
                    {busy === `${it.cita_id}:recordatorio`
                      ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      : <MessageCircle className="w-4 h-4 mr-1" />}
                    Recordatorio
                  </Button>
                  {centrosResena.length > 0 && (
                  <Button
                    size="sm" variant="outline"
                    className="h-8 text-amber-800 border-amber-300 hover:bg-amber-50"
                    onClick={() => (centrosResena.length === 1
                      ? enviarResena(it, centrosResena[0].id)
                      : setResenaFor(it))}
                    disabled={bloqueado}
                    title={centrosResena.length === 1
                      ? `Pedirle una reseña de Google (${centrosResena[0].nombre})`
                      : 'Pedirle una reseña de Google (se elige el centro)'}
                    data-testid={`recordatorio-resena-${it.cita_id}`}
                  >
                    {busy === `${it.cita_id}:resena`
                      ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      : <Star className="w-4 h-4 mr-1" />}
                    Pedir reseña
                  </Button>
                  )}
                  {it.tiene_email && data.email_automatico && (
                    <Button size="sm" variant="outline" className="h-8" onClick={() => enviarEmail(it)} disabled={enviandoEmail === it.cita_id} title="Enviar por email" data-testid={`recordatorio-email-${it.cita_id}`}>
                      {enviandoEmail === it.cita_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                    </Button>
                  )}
                  {it.enlace_confirmacion && (
                    <Button size="sm" variant="ghost" className="h-8" onClick={() => copiarEnlace(it)} title="Copiar enlace de confirmación" aria-label="Copiar enlace">
                      <Copy className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>

    {/* ¿En qué centro va la reseña? Mismo diálogo que en la ficha del
        paciente (2026-08-25/26: «se puede elegir»). Los centros son los de
        Configuración, cada uno con su enlace. */}
    <Dialog open={!!resenaFor} onOpenChange={(v) => !v && setResenaFor(null)}>
      <DialogContent className="max-w-sm" data-testid="recordatorio-resena-clinica-dialog">
        <DialogHeader>
          <DialogTitle>Pedir reseña de Google</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground -mt-2">
          ¿En qué ficha de Google quieres que deje la reseña {resenaFor?.patient_name}?
        </p>
        <div className="grid gap-2">
          {centrosResena.map((c) => (
            <Button
              key={c.id}
              type="button"
              className="bg-slate-700 hover:bg-slate-800 justify-start"
              onClick={() => enviarResena(resenaFor, c.id)}
              data-testid={`recordatorio-resena-centro-${c.id}`}
            >
              <Star className="w-4 h-4 mr-2" /> {c.nombre}
            </Button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
